/**
 * Editable Shadow DOM styles for <shopify-accelerated-checkout-cart>
 *
 * Requires layout/theme.liquid to run the attachShadow patch BEFORE content_for_header
 * (closed → open). Without that patch, shadowRoot is inaccessible and this file does nothing.
 *
 * How to edit:
 * 1. Change DEFAULT_SHADOW_CSS below, or
 * 2. In the browser console (or a small inline script): set
 *      window.__SHOPIFY_ACC_CART_SHADOW_CSS = `...your css...`;
 *    then call window.__refreshShopifyAccCartShadowStyles()
 *
 * Debug: add ?phi_acc_debug=1 to the URL
 */
(() => {
  const HOST_TAG = 'shopify-accelerated-checkout-cart';
  const STYLE_ID = 'phi-accelerated-checkout-cart-shadow-style';
  const POLL_MS = 120;
  const POLL_MAX_MS = 25000;

  /** @type {string} Edit this block in the theme asset */
  const DEFAULT_SHADOW_CSS = `
    :host {
      --_phi-acc-radius: var(--shopify-accelerated-checkout-button-border-radius, 0px);
      --_phi-acc-shadow: var(--shopify-accelerated-checkout-button-box-shadow, none);
    }

    .wallet-button-wrapper {
      display: block;
      margin-top: 1.2rem;
    }

    .wallet-cart-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 10px;
    }

    @media (max-width: 749px) {
      .wallet-cart-grid {
        grid-template-columns: 1fr;
      }
    }

    .wallet-cart-button-container {
      min-width: 0;
    }
    .wallet-button-wrapper {
        display: block;
        margin-top: 0;
    }
    .wallet-cart-grid > .wallet-cart-button-container {
      margin: 0 !important;
    }

    /* Only 2nd wallet cell visible (Shop Pay / PayPal / Google Pay order may vary by market) */
    .wallet-cart-grid.accelerated-checkout-button-container
      > .wallet-cart-button-container:not(:nth-child(2)),
    .accelerated-checkout-button-container .wallet-cart-grid
      > .wallet-cart-button-container:not(:nth-child(2)),
    .wallet-cart-grid > .wallet-cart-button-container:not(:nth-child(2)) {
      display: none !important;
    }

    .wallet-cart-button-container:nth-child(2) {
      display: block !important;
    }

    .wallet-cart-button {
      border-radius: var(--_phi-acc-radius);
      overflow: hidden;
      box-shadow: var(--_phi-acc-shadow);
    }

    .screen-reader-text {
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    }
  `.trim();

  function getEffectiveCss() {
    if (typeof window.__SHOPIFY_ACC_CART_SHADOW_CSS === 'string' && window.__SHOPIFY_ACC_CART_SHADOW_CSS.length) {
      if (window.__SHOPIFY_ACC_CART_SHADOW_APPEND === true) {
        return DEFAULT_SHADOW_CSS + '\n' + window.__SHOPIFY_ACC_CART_SHADOW_CSS;
      }
      return window.__SHOPIFY_ACC_CART_SHADOW_CSS;
    }
    return DEFAULT_SHADOW_CSS;
  }

  const DEBUG =
    typeof window !== 'undefined' &&
    (window.location.search.includes('phi_acc_debug=1') || window.__PHI_ACC_CART_DEBUG === true);

  function log(...args) {
    if (DEBUG) console.log('[shopify-acc-cart-shadow]', ...args);
  }

  function ensureStyleInRoot(root) {
    if (!root) return false;
    const css = getEffectiveCss();
    let styleEl = root.getElementById(STYLE_ID);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = STYLE_ID;
      styleEl.textContent = css;
      root.appendChild(styleEl);
    } else if (styleEl.textContent !== css) {
      styleEl.textContent = css;
    }
    return true;
  }

  function injectAllShadowRoots(root, depth) {
    if (!root || depth > 8) return;
    ensureStyleInRoot(root);
    root.querySelectorAll('*').forEach((node) => {
      if (node.shadowRoot) {
        injectAllShadowRoots(node.shadowRoot, depth + 1);
      }
    });
  }

  function watchShadowTree(el) {
    if (!el.shadowRoot || el.__phiAccShadowMO) return;
    el.__phiAccShadowMO = new MutationObserver(() => {
      if (el.shadowRoot) injectAllShadowRoots(el.shadowRoot, 0);
    });
    el.__phiAccShadowMO.observe(el.shadowRoot, { childList: true, subtree: true });
  }

  function attachToHost(el) {
    if (!el || el.tagName !== HOST_TAG.toUpperCase()) return;

    const tryInject = () => {
      if (!el.shadowRoot) return false;
      injectAllShadowRoots(el.shadowRoot, 0);
      watchShadowTree(el);
      return true;
    };

    if (tryInject()) {
      log('injected', el);
      return;
    }

    if (el.__phiAccPollActive || el.__phiAccGaveUp) return;

    el.__phiAccPollActive = true;
    log('waiting for shadowRoot…');

    const start = Date.now();
    const id = setInterval(() => {
      if (tryInject()) {
        clearInterval(id);
        el.__phiAccPollActive = false;
        log('injected after delay');
        return;
      }
      if (Date.now() - start > POLL_MAX_MS) {
        clearInterval(id);
        el.__phiAccPollActive = false;
        el.__phiAccGaveUp = true;
        log(
          'shadowRoot still null — attachShadow patch missing, or element uses a different API.'
        );
      }
    }, POLL_MS);
  }

  function scan() {
    document.querySelectorAll(HOST_TAG).forEach((el) => {
      attachToHost(el);
    });
  }

  function refreshAll() {
    document.querySelectorAll(HOST_TAG).forEach((el) => {
      if (el.shadowRoot) injectAllShadowRoots(el.shadowRoot, 0);
    });
  }

  window.__refreshShopifyAccCartShadowStyles = refreshAll;

  function boot() {
    const run = () => scan();

    if (window.customElements && typeof customElements.whenDefined === 'function') {
      customElements.whenDefined(HOST_TAG).then(run).catch(run);
    }
    run();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', scan, { once: true });
    }

    const docObserver = new MutationObserver(() => scan());
    docObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  boot();
})();
